import { PageHeader } from '@ant-design/pro-layout'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { type CompiledKpiPivotViewConfig, CORE_CONSTANT } from '@salescore/core'
import { message } from 'antd'
import { t } from 'i18next'
import { type ReactElement, useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'

import { usePivotColumns } from '../../hooks/usePivotColumns'
import { kpiParameterAtom } from '../../recoil/navigation/atoms'
import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'
import { ExpandableSider } from './ExpandableSider'
import { KpiPivotKpisForm } from './ViewUIKpiPivot/KpiPivotKpisFormModal'
import { KpiPivotNavigation } from './ViewUIKpiPivot/KpiPivotNavigation'
import { KpiPivotParameterSider } from './ViewUIKpiPivot/KpiPivotParameterSider'
import { ViewUIKpiPivotBody } from './ViewUIKpiPivot/ViewUIKpiPivotBody'

export const ViewUIKpiPivot = ({
  compiledConfig,
  view,
  ability,
  hasChangeToKpiPivotParameter,
  setHasChangeToKpiPivotParameter,
  setHasChangeToViewSchema,
}: {
  compiledConfig: CompiledKpiPivotViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  hasChangeToKpiPivotParameter: boolean
  setHasChangeToKpiPivotParameter: (hasChange: boolean) => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const [component] = compiledConfig.ui
  const [visibility, setVisibility] = useState(false)
  const [kpiPivotParameter, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const { systemColumns, rowColumns, pivotColumns } = usePivotColumns(compiledConfig, view)
  const columns = [...systemColumns, ...rowColumns, ...pivotColumns]
  // pivotにKPIが含まれていない時、強制的にKPIを含める
  useEffect(() => {
    const ds = [...kpiPivotParameter.pivot.rows, ...kpiPivotParameter.pivot.columns]
    if (ds.every((x) => x.key !== CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key)) {
      setKpiPivotParameter((oldValue) => ({
        ...oldValue,
        pivot: {
          ...oldValue.pivot,
          columns: [...oldValue.pivot.columns, CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION],
        },
      }))
    }
  }, [kpiPivotParameter])

  if (component.kpis.length === 0) {
    return (
      <PageHeader title={t(`ダッシュボード設定`)} className="h-full bg-white">
        <div style={{ maxWidth: 700 }}>
          <KpiPivotKpisForm
            onFinish={() => {
              void message.success(t(`KPIを設定しました`))
            }}
            columns={columns}
            view={view}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
        </div>
      </PageHeader>
    )
  }

  return (
    <div className="h-full">
      <ExpandableSider
        visibility={visibility}
        width={430}
        sider={
          <KpiPivotParameterSider
            component={component}
            close={() => {
              setVisibility(false)
            }}
            columns={columns}
            view={view}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
        }
        body={
          <div className="flex h-full flex-col overflow-y-auto bg-white">
            <KpiPivotNavigation
              component={component}
              setSiderVisibility={setVisibility}
              columns={columns}
              compiledConfig={compiledConfig}
              view={view}
              ability={ability}
              hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
              setHasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
              setHasChangeToViewSchema={setHasChangeToViewSchema}
            />

            <div
              className="min-h-0 flex-1 border"
              // ここでkey指定するとviewの切り替え時に全レンダリングが走って良くないが、実装ミスでレンダリングが微妙になるのを防ぐのを優先する
              key={view.id}
            >
              <ViewUIKpiPivotBody
                component={component}
                columns={columns}
                compiledConfig={compiledConfig}
                view={view}
                ability={ability}
                setHasChangeToViewSchema={setHasChangeToViewSchema}
              />
            </div>
          </div>
        }
      />
    </div>
  )
}
